import { ApiError } from '@lib/api';
import { cn } from '@lib/cn';
import {
    Button,
    Modal,
    PropagationNotice,
    SelectField,
    TextAreaField,
    TextField,
    WriteError,
} from '@shared/ui';
import { useId, useState } from 'react';

import {
    createFlagInputFrom,
    EMPTY_CREATE_DRAFT,
    type CreateDraft,
} from '../api/definition-create';
import { useCreateFlag } from '../hooks/use-flags';
import { explainFlagKeyRejection, rejectFlagKey } from '../schemas/flag-key';
import {
    explainSalesforceFieldRejection,
    FLAG_TYPE_DESCRIPTION,
    FLAG_TYPE_LABEL,
    FLAG_TYPE_ORDER,
    rejectSalesforceField,
} from '../schemas/flag-type';
import { PURPOSE_LABEL, PURPOSE_OPTIONS } from '../schemas/purpose';

import type { FlagLifecycle, FlagType } from '../schemas/flag';

/**
 * Create a flag at runtime — the action that has no equivalent in
 * VeevartBackend, where a flag exists because someone merged a PR.
 *
 * ## The confirm step is the point of this dialog
 *
 * Losing the code-owned catalog lost one safety property that nothing else
 * replaces: **a typo'd key is not an error.** Upstream, `checkout.new-chekout`
 * fails to compile. Here it creates a second flag, sitting in the catalog
 * looking exactly as legitimate as the one that was meant, gating nothing, and
 * un-renameable — keys are never reused, because defaults, overrides and audit
 * rows are all filed under them.
 *
 * So the flow is deliberately two steps. The second screen exists only to put
 * the key in front of the operator, on its own, after they have stopped typing,
 * and make them agree to it. It is the last human check before a permanent row.
 *
 * ## The type comes first, and is never preselected
 *
 * The type is as permanent as the key and decides who reads the flag at all
 * (ADR 0009 §5). It is asked first because it shapes the rest of the form — a
 * `salesforce` flag needs its target field — and it starts unchosen because a
 * default is a guess the operator can click past, and a wrong one is a key
 * burned.
 *
 * ## Classification is its own section, and purpose goes second in it
 *
 * The form opens with a classification section holding the type and then the
 * flag's **purpose** (`release`, `operational`, `entitlement`), straight after
 * it and before anything the operator types: purpose is what decides whether
 * the flag is expected to be removed, so it frames the key, the description and
 * the lifecycle below it.
 *
 * Purpose was read-only for six sprints — displayed in the list and the detail
 * page, settable only by editing a flag that already existed, which meant every
 * flag was born `release` and reclassified afterwards if anyone remembered. It
 * is written here now. The body is always sent with a value, never omitted for
 * the API to default: the audit entry then records the purpose that was on
 * screen rather than the one the server chose. See `api/definition-create.ts`,
 * which owns the payload because nothing rendered in this workspace is testable.
 *
 * The four values this docblock first proposed became three. `experiment` was
 * dropped: on this axis it answers the same as `release`, and `lifecycle` at
 * `dev`/`beta` already says a flag is an experiment. `permission` was deferred
 * rather than rejected — authorization may well be distinct from commercial
 * entitlement, but nothing can yet say what activating such a flag would do,
 * and `purpose` dispatches behaviour instead of labelling rows.
 */

const LIFECYCLE_OPTIONS: readonly { value: FlagLifecycle; label: string }[] = [
    { value: 'dev', label: 'Dev — not ready for anyone' },
    { value: 'beta', label: 'Beta — selected museums' },
    { value: 'ga', label: 'GA — generally available' },
    { value: 'deprecated', label: 'Deprecated — stop using' },
];

/**
 * The draft and the body it becomes both live in `api/definition-create.ts`.
 * Nothing rendered is testable in this workspace — vitest runs on `node` with
 * no DOM — so the payload sits where a test can see it, and this file holds
 * only what it draws.
 */
type Draft = CreateDraft;

const EMPTY_DRAFT = EMPTY_CREATE_DRAFT;

export function CreateFlagDialog({
    open,
    onClose,
    onCreated,
}: {
    open: boolean;
    onClose: () => void;
    onCreated: (key: string) => void;
}) {
    const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
    const [confirming, setConfirming] = useState(false);
    const create = useCreateFlag();

    const set = <K extends keyof Draft>(field: K, value: Draft[K]) => {
        setDraft((current) => ({ ...current, [field]: value }));
    };

    const close = () => {
        setDraft(EMPTY_DRAFT);
        setConfirming(false);
        create.reset();
        onClose();
    };

    const isSalesforce = draft.type === 'salesforce';

    const rejection = rejectFlagKey(draft.key);
    // An untouched field is not yet a mistake — showing "a key is required" on
    // an empty form scolds the operator for not having typed anything yet.
    const keyError =
        draft.key.length > 0 && rejection ? explainFlagKeyRejection(rejection) : undefined;

    const fieldRejection = rejectSalesforceField(draft.salesforceField);
    const fieldError =
        draft.salesforceField.length > 0 && fieldRejection
            ? explainSalesforceFieldRejection(fieldRejection)
            : undefined;

    const complete =
        draft.type !== null &&
        rejection === null &&
        (!isSalesforce || fieldRejection === null) &&
        draft.name.trim().length > 0 &&
        draft.description.trim().length > 0 &&
        draft.category.trim().length > 0 &&
        draft.owner.trim().length > 0;

    const submit = () => {
        const input = createFlagInputFrom(draft);
        if (input === null) return;

        create.mutate(input, {
            onSuccess: (definition) => {
                setDraft(EMPTY_DRAFT);
                setConfirming(false);
                onCreated(definition.key);
            },
            // A taken key or a taken Salesforce field are the failures the
            // operator fixes by editing a field, so send them back to the
            // form rather than leaving them on a confirm screen whose only
            // button re-fails.
            onError: (error) => {
                if (
                    error instanceof ApiError &&
                    (error.isAlreadyExists || error.isSalesforceFieldTaken)
                ) {
                    setConfirming(false);
                }
            },
        });
    };

    return (
        <Modal
            open={open}
            onClose={close}
            dismissable={!create.isPending}
            title={confirming ? 'Create this flag?' : 'New feature flag'}
            description={
                confirming
                    ? 'Keys and types cannot be changed later — defaults, overrides and audit entries are all filed under them. Check both read exactly as you intend.'
                    : 'The flag is created switched off, so this cannot change any behaviour on its own.'
            }
            footer={
                confirming ? (
                    <>
                        <Button
                            variant="ghost"
                            onClick={() => {
                                setConfirming(false);
                            }}
                            disabled={create.isPending}
                        >
                            Back
                        </Button>
                        <Button onClick={submit} disabled={create.isPending}>
                            {create.isPending ? 'Creating…' : 'Create flag'}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button variant="ghost" onClick={close}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                setConfirming(true);
                            }}
                            disabled={!complete}
                        >
                            Review
                        </Button>
                    </>
                )
            }
        >
            {confirming && draft.type !== null ? (
                <div className="flex flex-col gap-4">
                    <div className="rounded-lg border border-accent-line bg-accent-surface px-4 py-3.5">
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-accent-text">
                            Create {FLAG_TYPE_LABEL[draft.type]} flag
                        </div>
                        <div className="mt-1.5 break-all font-mono text-[15px] font-semibold text-content">
                            {draft.key.trim()}
                        </div>
                    </div>

                    <dl className="text-[13px]">
                        <Row label="Type">{FLAG_TYPE_LABEL[draft.type]}</Row>
                        {isSalesforce && (
                            <Row label="Salesforce field">
                                <span className="font-mono">{draft.salesforceField.trim()}</span>
                            </Row>
                        )}
                        <Row label="Purpose">{PURPOSE_LABEL[draft.purpose]}</Row>
                        <Row label="Name">{draft.name.trim()}</Row>
                        <Row label="Category">{draft.category.trim()}</Row>
                        <Row label="Owner">{draft.owner.trim()}</Row>
                        <Row label="Lifecycle">{draft.lifecycle}</Row>
                        <Row label="Initial state">
                            {isSalesforce ? 'Off in every org' : 'Off, no rollout gate'}
                        </Row>
                    </dl>

                    {create.error && <WriteError error={create.error} />}

                    {isSalesforce ? (
                        <p className="rounded-lg bg-surface-sunken px-3.5 py-2.5 text-[12px] leading-relaxed text-content-subtle">
                            Its value is projected into customers&apos; Salesforce orgs as{' '}
                            <code className="font-mono">{draft.salesforceField.trim()}</code> on{' '}
                            <code className="font-mono">V2_Feature_Flags__c</code>. A field the org
                            does not have is refused by Salesforce when the value is published, not
                            here.
                        </p>
                    ) : (
                        <p className="rounded-lg bg-surface-sunken px-3.5 py-2.5 text-[12px] leading-relaxed text-content-subtle">
                            Creating a flag here does not make it gate anything yet — VeevartBackend
                            resolves it <strong className="font-semibold">off</strong> until its
                            read path reads this catalog. Referencing the key in code is a separate
                            step.
                        </p>
                    )}
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    <ClassificationSection>
                        <FlagTypeChoice
                            value={draft.type}
                            onChange={(value) => {
                                set('type', value);
                            }}
                        />
                        {/* After the type, before anything typed: it decides
                            whether the flag is ever meant to be deleted, which
                            frames the key and the lifecycle below it. */}
                        <SelectField
                            label="Purpose"
                            value={draft.purpose}
                            onChange={(value) => {
                                set('purpose', value);
                            }}
                            options={PURPOSE_OPTIONS}
                            hint="What the flag exists for. It can be changed later, unlike the key and the type."
                        />
                    </ClassificationSection>
                    <TextField
                        label="Key"
                        value={draft.key}
                        onChange={(value) => {
                            set('key', value);
                        }}
                        placeholder="checkout.new-flow"
                        mono
                        error={keyError}
                        hint="Lowercase, dot-separated. This is permanent — it cannot be renamed."
                    />
                    {isSalesforce && (
                        <TextField
                            label="Salesforce field"
                            value={draft.salesforceField}
                            onChange={(value) => {
                                set('salesforceField', value);
                            }}
                            placeholder="Checkout_New_Flow__c"
                            mono
                            error={fieldError}
                            hint="The V2_Feature_Flags__c field this flag's value is projected to. One field belongs to one flag."
                        />
                    )}
                    <TextField
                        label="Name"
                        value={draft.name}
                        onChange={(value) => {
                            set('name', value);
                        }}
                        placeholder="New checkout flow"
                    />
                    <TextAreaField
                        label="What it gates"
                        value={draft.description}
                        onChange={(value) => {
                            set('description', value);
                        }}
                        placeholder="What breaks if this is turned off?"
                        hint="This row is the only place a flag's purpose is recorded now that no code catalog holds one."
                    />
                    <div className="grid grid-cols-2 gap-4">
                        <TextField
                            label="Category"
                            value={draft.category}
                            onChange={(value) => {
                                set('category', value);
                            }}
                            placeholder="Payments"
                        />
                        <TextField
                            label="Owning team"
                            value={draft.owner}
                            onChange={(value) => {
                                set('owner', value);
                            }}
                            placeholder="Payments"
                        />
                    </div>
                    <SelectField
                        label="Lifecycle"
                        value={draft.lifecycle}
                        onChange={(value) => {
                            set('lifecycle', value);
                        }}
                        options={LIFECYCLE_OPTIONS}
                        hint="A label for operators. The switch that actually stops a flag being served is Archived, on the default."
                    />
                    <TextAreaField
                        label="Reason (optional)"
                        value={draft.reason}
                        onChange={(value) => {
                            set('reason', value);
                        }}
                        rows={2}
                        placeholder="Why this flag is being created"
                        hint="Recorded on the audit entry."
                    />

                    {create.error && <WriteError error={create.error} />}

                    <PropagationNotice />
                </div>
            )}
        </Modal>
    );
}

/**
 * What kind of flag this is, asked before any of its details. Holds the type
 * today and the purpose once that attribute exists; separated from the
 * details below so the classification reads as one decision made first.
 */
function ClassificationSection({ children }: { children: React.ReactNode }) {
    return (
        <section
            aria-label="Classification"
            className="flex flex-col gap-4 border-b border-line-subtle pb-4"
        >
            {children}
        </section>
    );
}

/**
 * Two cards rather than a `<select>`: the choice is between two consumers, and
 * the one-line description of each is what makes it a decision instead of a
 * label picked by habit.
 */
function FlagTypeChoice({
    value,
    onChange,
}: {
    value: FlagType | null;
    onChange: (next: FlagType) => void;
}) {
    const name = useId();

    return (
        <fieldset className="flex flex-col gap-1.5">
            <legend className="mb-1.5 text-[12.5px] font-semibold text-content-body">Type</legend>
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                {FLAG_TYPE_ORDER.map((type, index) => (
                    <label
                        key={type}
                        className={cn(
                            'flex cursor-pointer flex-col gap-1 rounded-lg border px-3.5 py-3 transition-colors',
                            value === type
                                ? 'border-brand bg-accent-surface'
                                : 'border-line hover:border-line-strong',
                        )}
                    >
                        <span className="flex items-center gap-2 text-[13px] font-semibold text-content">
                            <input
                                type="radio"
                                name={name}
                                value={type}
                                checked={value === type}
                                onChange={() => {
                                    onChange(type);
                                }}
                                // See `TextField`: a marker `Modal` focuses after
                                // `showModal()`, since the type is asked first.
                                data-autofocus={index === 0 ? '' : undefined}
                            />
                            {FLAG_TYPE_LABEL[type]}
                        </span>
                        <span className="text-[12px] leading-relaxed text-content-muted">
                            {FLAG_TYPE_DESCRIPTION[type]}
                        </span>
                    </label>
                ))}
            </div>
            <p className="text-[12px] leading-relaxed text-content-muted">
                Permanent. A flag that needs to mean something else is a new key.
            </p>
        </fieldset>
    );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="flex items-baseline justify-between gap-4 border-b border-line-subtle py-2 last:border-0">
            <dt className="shrink-0 text-content-muted">{label}</dt>
            <dd className="text-right font-medium text-content">{children}</dd>
        </div>
    );
}
